// Page des locataires par résidence avec design amélioré
// Fichier: /var/www/squarecom/frontend/src/pages/ResidenceTenants.js

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  Button,
  IconButton,
  TextField,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Tooltip,
  Snackbar,
  Alert,
  Pagination,
  CircularProgress,
  Avatar,
  InputAdornment,
  TableContainer,
  Container,
  useTheme,
  alpha
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  ContentCopy as ContentCopyIcon,
  Search as SearchIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Person as PersonIcon
} from '@mui/icons-material';

// Fonction pour générer une couleur basée sur le nom
function stringToColor(string) {
  let hash = 0;
  let i;
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = '#';
  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }
  return color;
}

// Fonction pour obtenir les initiales
function getInitials(firstName, lastName) {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
}

// Fonction pour exporter en CSV
function toCSV(rows, residenceName) {
  const headers = ['Prénom', 'Nom', 'Email', 'Téléphone', 'Appartement', 'Résidence'];
  const escape = (v) => {
    if (v == null) return '';
    const s = String(v);
    if (s.includes(';') || s.includes('"') || s.includes('\n')) {
      return `"${s.replace(/"/g, '""')}"`;
    }
    return s;
  };
  const lines = [headers.join(';')];
  for (const r of rows) {
    lines.push(
      [r.firstName, r.lastName, r.email, r.phone, r.apartmentNumber, residenceName]
        .map(escape)
        .join(';')
    );
  }
  return lines.join('\n');
}

export default function ResidenceTenants() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [residenceName, setResidenceName] = useState('');
  const [search, setSearch] = useState('');
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'success' });
  
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token') || '';
      
      // Charger le nom de la résidence
      const resRes = await fetch(`/api/residences`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      if (resRes.ok) {
        const resData = await resRes.json();
        const residence = resData.residences?.find(r => r.id === id);
        if (residence) setResidenceName(residence.name);
      }

      // Charger les locataires
      const res = await fetch(`/api/tenants?residenceId=${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const j = await res.json();
      const data = j?.tenants || [];
      setTenants(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('Erreur:', e);
      setSnack({ open: true, message: `Erreur: ${e.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Filtrage par recherche
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tenants;
    return tenants.filter((t) => {
      const hay = `${t.firstName} ${t.lastName} ${t.email} ${t.phone} ${t.apartmentNumber}`.toLowerCase();
      return hay.includes(q);
    });
  }, [search, tenants]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const pageRows = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);

  const handleExport = () => {
    const csv = toCSV(filtered, residenceName);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `locataires-${residenceName.replace(/\s+/g, '-').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setSnack({ open: true, message: 'Export CSV réussi', severity: 'success' });
  };

  const handleCopyEmails = async () => {
    try {
      const emails = filtered.map((t) => t.email).filter(Boolean).join(', ');
      if (!emails) {
        setSnack({ open: true, message: 'Aucune adresse e-mail à copier', severity: 'warning' });
        return;
      }
      await navigator.clipboard.writeText(emails);
      setSnack({ open: true, message: 'Adresses e-mail copiées dans le presse-papiers', severity: 'success' });
    } catch (e) {
      setSnack({ open: true, message: `Impossible de copier: ${e.message}`, severity: 'error' });
    }
  };

  if (loading && tenants.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box py={3}>
        {/* Header avec bouton retour */}
        <Stack direction="row" alignItems="center" spacing={2} mb={4}>
          <IconButton 
            onClick={() => navigate('/residences')}
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
            }}
          >
            <ArrowBackIcon />
          </IconButton>
          
          <Box flex={1}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="h4" fontWeight={600}>
                Locataires - {residenceName}
              </Typography>
              <Chip
                label={`${filtered.length} locataire${filtered.length > 1 ? 's' : ''}`}
                color="primary"
                variant="outlined"
              />
            </Stack>
          </Box>

          <Stack direction="row" spacing={1}>
            <Tooltip title="Rafraîchir">
              <IconButton
                onClick={loadData}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Copier les emails">
              <IconButton
                onClick={handleCopyEmails}
                sx={{
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  '&:hover': { bgcolor: alpha(theme.palette.info.main, 0.2) }
                }}
              >
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExport}
              sx={{ borderRadius: 2 }}
            >
              EXPORTER CSV
            </Button>
          </Stack>
        </Stack>

        {/* Barre de recherche */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            bgcolor: alpha(theme.palette.background.paper, 0.8)
          }}
        >
          <TextField
            fullWidth
            placeholder="Rechercher (nom, email, téléphone, appartement)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {/* Table */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            overflow: 'hidden'
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
                <TableCell sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PersonIcon fontSize="small" />
                    <Typography>Locataire</Typography>
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <EmailIcon fontSize="small" sx={{ color: 'action.active' }} />
                    <Typography>Email</Typography>
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <PhoneIcon fontSize="small" sx={{ color: 'action.active' }} />
                    <Typography>Téléphone</Typography>
                  </Stack>
                </TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <HomeIcon fontSize="small" sx={{ color: 'action.active' }} />
                    <Typography>Appartement</Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {pageRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="textSecondary">
                      {search ? 'Aucun résultat trouvé' : 'Aucun locataire dans cette résidence'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                pageRows.map((tenant, index) => (
                  <TableRow
                    key={tenant.id || index}
                    hover
                    sx={{
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.02)
                      }
                    }}
                  >
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <Avatar
                          sx={{
                            bgcolor: stringToColor(`${tenant.firstName} ${tenant.lastName}`),
                            width: 40,
                            height: 40,
                            fontSize: '0.9rem'
                          }}
                        >
                          {getInitials(tenant.firstName, tenant.lastName)}
                        </Avatar>
                        <Box>
                          <Typography fontWeight={500}>
                            {tenant.firstName} {tenant.lastName}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>

                    <TableCell>
                      {tenant.email ? (
                        <Typography
                          component="a"
                          href={`mailto:${tenant.email}`}
                          sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' }
                          }}
                        >
                          {tenant.email}
                        </Typography>
                      ) : (
                        <Typography color="textSecondary">—</Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      {tenant.phone ? (
                        <Typography
                          component="a"
                          href={`tel:${tenant.phone}`}
                          sx={{
                            color: 'text.primary',
                            textDecoration: 'none',
                            '&:hover': { color: 'primary.main' }
                          }}
                        >
                          {tenant.phone}
                        </Typography>
                      ) : (
                        <Typography color="textSecondary">—</Typography>
                      )}
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={tenant.apartmentNumber || '—'}
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.info.main, 0.1),
                          color: theme.palette.info.dark,
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, p) => setPage(p)}
              color="primary"
              shape="rounded"
            />
          </Box>
        )}

        {/* Snackbar pour les notifications */}
        <Snackbar
          open={snack.open}
          autoHideDuration={3000}
          onClose={() => setSnack({ ...snack, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnack({ ...snack, open: false })}
            severity={snack.severity}
            variant="filled"
          >
            {snack.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
}
